import { BasicButton, Typography } from "@/components";
import { BackAppBar } from "@/components/ui";
import useBackward from "@/hooks/useBackward";
import useTeamMutation from "@/hooks/useTeamMutation";
import { cn } from "@/libraries/cn";
import { getTeamQueryOptions } from "@/queries";
import { UpdateTeamFieldSchema } from "@/schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback } from "react";
import { Controller, type SubmitHandler } from "react-hook-form";
import { TextFieldElement, useForm } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/dashboard/teams/$id/edit/")({
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation(["dashboard", "common"]);
	const teamId = Route.useParams({
		select: (state) => state.id,
	});

	const onBack = useBackward();

	const { update } = useTeamMutation();

	const {
		data: { team },
	} = useSuspenseQuery(getTeamQueryOptions(teamId));

	const {
		control,
		handleSubmit,
		formState: { isDirty, isValid, isSubmitting },
	} = useForm<UpdateTeamFieldSchema>({
		mode: "all",
		resolver: zodResolver(UpdateTeamFieldSchema),
		defaultValues: {
			id: team.id,
			name: team.name ?? "",
			directory: team.directory ?? false,
		},
	});

	const onSubmit = useCallback<SubmitHandler<UpdateTeamFieldSchema>>(
		async (data, e) => {
			e?.stopPropagation();
			e?.preventDefault();
			try {
				await update.mutateAsync(data);
				return onBack();
			} catch (error) {
				return toast.error(t("dashboard:teams.updateFailed"));
			}
		},
		[update, onBack, t],
	);

	return (
		<div className="h-full flex flex-col overflow-hidden">
			<BackAppBar title={t("dashboard:teams.editTeamName")} />
			<form
				onSubmit={handleSubmit(onSubmit)}
				className="h-full flex-1 flex flex-col"
			>
				<div className="flex flex-1 flex-col">
					<div className="container py-4">
						<TextFieldElement
							fullWidth
							control={control}
							label={t("dashboard:teams.teamName")}
							name="name"
							className="[&_.MuiInputBase-root]:!rounded-xl"
						/>
					</div>
					<Divider />
					<div className="container py-4">
						<div className="flex flex-col gap-4">
							<Typography fontWeight={700} fontSize={14}>
								{t("dashboard:teams.addToDirectory")}
							</Typography>
							<Controller
								control={control}
								name="directory"
								defaultValue={false}
								render={({ field }) => (
									<>
										<Stack
											direction="row"
											spacing={2}
											justifyContent="center"
											alignItems="center"
										>
											<BasicButton
												variant="outlined"
												color={field.value === true ? "info" : "inherit"}
												className={cn(
													"w-full !h-12 !text-base font-medium !shadow-none",
													{
														"!bg-primary-light": field.value,
													},
												)}
												onClick={() => field.onChange(true)}
												onBlur={field.onBlur}
											>
												<Box
													component="span"
													className="flex items-center gap-2"
												>
													<CheckIcon />
													<span>{t("dashboard:teams.yes")}</span>
												</Box>
											</BasicButton>
											<BasicButton
												variant="outlined"
												color={field.value === false ? "error" : "inherit"}
												className={cn(
													"w-full !h-12 !text-base font-medium !shadow-none",
													{
														"!bg-danger-100": !field.value,
													},
												)}
												onClick={() => field.onChange(false)}
												onBlur={field.onBlur}
											>
												<Box
													component="span"
													className="flex items-center gap-2"
												>
													<CloseIcon />
													<span>{t("dashboard:teams.no")}</span>
												</Box>
											</BasicButton>
										</Stack>
										<Box className="flex items-start gap-1">
											<InfoRoundedIcon className="!text-base" />
											<p className="text-dark text-xs font-medium">
												{field.value
													? t("dashboard:teams.directoryYesHintTeam")
													: t("dashboard:teams.directoryNoHintTeam")}
											</p>
										</Box>
									</>
								)}
							/>
						</div>
					</div>
				</div>
				<div className="container py-4 shadow flex flex-row gap-4 justify-center items-center">
					<BasicButton
						variant="outlined"
						color="secondary"
						className="w-full !h-12 text-sm font-semibold !shadow-none"
						onClick={() => onBack()}
					>
						{t("common:cancel")}
					</BasicButton>
					<BasicButton
						type="submit"
						variant="contained"
						color="secondary"
						disabled={!isDirty || !isValid}
						loading={isSubmitting}
						className="w-full !h-12 text-sm font-semibold !shadow-none"
					>
						{t("dashboard:teams.update")}
					</BasicButton>
				</div>
			</form>
		</div>
	);
}
